import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ChevronRight, Share2, Facebook, Linkedin, MessageCircle, Check } from "lucide-react";
import { useArticles, useArticle } from "@/data/hooks";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp } from "@/components/Reveal";

const SITE_URL = "https://nanda-sa.com";

function useArticleSeo(a, locale) {
 React.useEffect(() => {
 if (!a) return;
 const title = (a.seo && a.seo.title) || `${a.title} | NANDA SÁ`;
 const desc = (a.seo && a.seo.description) || a.excerpt;
 const canonical = `${SITE_URL}/carnet/${a.slug}`;
 const img = a.cover?.startsWith("http") ? a.cover : `${SITE_URL}${a.cover}`;

 document.title = title;
 const set = (attr, key, content) => {
 let el = document.querySelector(`meta[${attr}="${key}"]`);
 if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
 el.setAttribute("content", content);
 };
 set("name", "description", desc);
 if (a.seo?.keywords) set("name", "keywords", a.seo.keywords);
 set("property", "og:type", "article");
 set("property", "og:title", title);
 set("property", "og:description", desc);
 set("property", "og:image", img);
 set("property", "og:url", canonical);
 set("name", "twitter:card", "summary_large_image");
 set("name", "twitter:title", title);
 set("name", "twitter:description", desc);
 set("name", "twitter:image", img);

 let link = document.querySelector('link[rel="canonical"]');
 if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
 link.setAttribute("href", canonical);
 }, [a, locale]);
}

export default function ArticlePage() {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => locale === "en" ? en : locale === "pt" ? pt : fr;
 const dateLocale = locale === "en" ? "en-GB" : locale === "pt" ? "pt-PT" : "fr-FR";
 const formatDate = (iso) => new Date(iso).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" });
 const { slug } = useParams();
 const a = useArticle(slug);
 const ARTICLES = useArticles();

 useArticleSeo(a, locale);

 if (!a) return <Navigate to="/carnet" replace />;

 const idx = ARTICLES.findIndex((x) => x.slug === a.slug);
 const next = ARTICLES[(idx + 1) % ARTICLES.length];

 // Extract H2 for TOC
 const toc = (a.sections || []).filter((s) => s.type === "h2");
 const shareUrl = `${SITE_URL}/carnet/${a.slug}`;
 const shareTitle = encodeURIComponent(a.title);

 // JSON-LD Article schema
 const articleSchema = {
 "@context": "https://schema.org",
 "@type": "BlogPosting",
 headline: a.title,
 description: a.excerpt,
 image: a.cover?.startsWith("http") ? a.cover : `${SITE_URL}${a.cover}`,
 datePublished: a.date,
 dateModified: a.date,
 inLanguage: { fr: "fr-FR", en: "en-GB", pt: "pt-PT" }[locale],
 author: { "@type": "Organization", name: "NANDA SÁ" },
 publisher: {
 "@type": "Organization",
 name: "NANDA SÁ",
 logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-nanda-sa.png` },
 },
 mainEntityOfPage: shareUrl,...(a.faq && a.faq.length ? {} : {}),
 };
 const faqSchema = a.faq && a.faq.length ? {
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: a.faq.map((f) => ({
 "@type": "Question",
 name: f.q,
 acceptedAnswer: { "@type": "Answer", text: f.a },
 })),
 } : null;
 const breadcrumbSchema = {
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "NANDA SÁ", item: `${SITE_URL}/` },
 { "@type": "ListItem", position: 2, name: l("Carnet", "Journal", "Caderno"), item: `${SITE_URL}/carnet` },
 { "@type": "ListItem", position: 3, name: a.title, item: shareUrl },
 ],
 };

 return (
 <div data-testid={`page-article-${a.slug}`}>
 <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
 <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
 {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}

 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-32 md:pt-40 pb-12">
 {/* Breadcrumbs */}
 <nav className="label-overline text-nanda-muted mb-8 flex items-center gap-2 flex-wrap" aria-label="Breadcrumb">
 <Link to="/" className="hover:text-nanda-ink transition-colors">{l("Accueil", "Home", "Início")}</Link>
 <ChevronRight size={11} strokeWidth={1.5} className="text-nanda-line" />
 <Link to="/carnet" className="hover:text-nanda-ink transition-colors">{l("Carnet", "Journal", "Caderno")}</Link>
 <ChevronRight size={11} strokeWidth={1.5} className="text-nanda-line" />
 <span className="text-nanda-ink truncate max-w-[60vw]">{a.title}</span>
 </nav>

 <div className="label-overline text-nanda-terracotta mb-6">
 {a.category} · {formatDate(a.date)} · {a.readTime} min · {l("Par", "By", "Por")} NANDA SÁ
 </div>
 <h1 className="font-sans font-light tracking-tight text-nanda-ink text-4xl sm:text-5xl md:text-6xl text-balance leading-[1.05]" data-testid="article-title">{a.title}</h1>
 <p className="mt-8 max-w-2xl text-nanda-inkSoft text-base md:text-lg leading-relaxed">{a.excerpt}</p>

 {/* Share */}
 <div className="mt-10 flex items-center gap-4 flex-wrap" data-testid="article-share">
 <span className="label-overline text-nanda-muted flex items-center gap-2"><Share2 size={12} /> {l("Partager", "Share", "Partilhar")}</span>
 <a href={`https://wa.me/?text=${shareTitle}%20${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="text-nanda-muted hover:text-nanda-terracotta transition-colors" data-testid="share-whatsapp">
 <MessageCircle size={16} strokeWidth={1.5} />
 </a>
 <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-nanda-muted hover:text-nanda-terracotta transition-colors" data-testid="share-facebook">
 <Facebook size={16} strokeWidth={1.5} />
 </a>
 <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="text-nanda-muted hover:text-nanda-terracotta transition-colors" data-testid="share-linkedin">
 <Linkedin size={16} strokeWidth={1.5} />
 </a>
 </div>
 </div>
 </section>

 {/* Cover image */}
 <section>
 <div className="mx-auto max-w-7xl px-6 sm:px-12 py-12 md:py-16">
 <FadeUp>
 <div className="aspect-[16/9] overflow-hidden rounded-3xl bg-nanda-bgAlt">
 <img src={a.cover} alt={a.coverAlt || a.title} loading="eager" className="h-full w-full object-cover" />
 </div>
 </FadeUp>
 </div>
 </section>

 {/* Body: sections[] enhanced OR legacy body[] */}
 {a.sections && a.sections.length > 0 ? (
 <section>
 <div className="mx-auto max-w-7xl px-6 sm:px-12 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
 {/* Table of Contents */}
 {toc.length > 0 && (
 <aside className="lg:col-span-3 order-2 lg:order-1">
 <div className="lg:sticky lg:top-24">
 <div className="label-overline text-nanda-terracotta mb-4">{l("Sommaire", "Contents", "Sumário")}</div>
 <ol className="space-y-2 border-l border-nanda-line pl-4" data-testid="article-toc">
 {toc.map((h, i) => (
 <li key={h.id}>
 <a href={`#${h.id}`} className="text-sm text-nanda-muted hover:text-nanda-ink transition-colors leading-snug block" data-testid={`toc-link-${h.id}`}>
 <span className="text-nanda-terracotta mr-2">{String(i + 1).padStart(2, "0")}</span>{h.text}
 </a>
 </li>
 ))}
 </ol>
 </div>
 </aside>
 )}

 {/* Main content */}
 <article className={`${toc.length > 0 ? "lg:col-span-9" : "lg:col-span-12"} order-1 lg:order-2 max-w-[760px]`}>
 {a.intro && (
 <div className="text-lg md:text-xl leading-[1.7] mb-12 font-light text-nanda-inkSoft border-l-2 border-nanda-terracotta pl-6 whitespace-pre-line">
 {a.intro}
 </div>
 )}
 {a.sections.map((s, i) => {
 if (s.type === "h2") {
 return (
 <h2 key={i} id={s.id} className="font-sans font-light tracking-tight text-3xl md:text-4xl leading-[1.1] text-nanda-ink mt-16 mb-6 scroll-mt-24" data-testid={`article-h2-${s.id}`}>
 {s.text}
 </h2>
 );
 }
 if (s.type === "h3") {
 return (
 <h3 key={i} className="font-sans font-medium tracking-tight text-xl md:text-2xl leading-tight text-nanda-ink mt-8 mb-3">
 {s.text}
 </h3>
 );
 }
 // paragraph, support bullet lines (•) and line breaks
 return (
 <div key={i} className="text-nanda-ink text-base md:text-lg leading-[1.75] mb-6 whitespace-pre-line">
 {s.text}
 </div>
 );
 })}

 {/* Internal links */}
 {a.internalLinks && a.internalLinks.length > 0 && (
 <div className="mt-16 border-t border-nanda-line pt-10">
 <div className="label-overline text-nanda-terracotta mb-6">{l("Aller plus loin", "Go further", "Ir mais longe")}</div>
 <ul className="space-y-3">
 {a.internalLinks.map((link, i) => (
 <li key={i}>
 <Link to={link.to} className="group inline-flex items-center gap-2 font-sans font-medium tracking-tight text-xl text-nanda-ink hover:text-nanda-terracotta transition-colors" data-testid={`article-internal-link-${i}`}>
 <ChevronRight size={16} className="text-nanda-terracotta" />
 {link.label}
 <ArrowUpRight size={14} className="opacity-60 group-hover:opacity-100" />
 </Link>
 </li>
 ))}
 </ul>
 </div>
 )}

 {/* FAQ */}
 {a.faq && a.faq.length > 0 && (
 <div className="mt-16 border-t border-nanda-line pt-10" data-testid="article-faq">
 <div className="label-overline text-nanda-terracotta mb-4">FAQ</div>
 <h2 className="font-sans font-light tracking-tight text-3xl md:text-4xl leading-[1.1] text-nanda-ink mb-8">
 {l("Questions fréquentes", "Frequently asked questions", "Perguntas frequentes")}
 </h2>
 <div className="divide-y divide-nanda-line border-t border-b border-nanda-line">
 {a.faq.map((f, i) => (
 <details key={i} className="group py-5" data-testid={`article-faq-${i}`}>
 <summary className="cursor-pointer flex items-center justify-between gap-4 font-sans font-medium tracking-tight text-lg text-nanda-ink hover:text-nanda-terracotta transition-colors list-none">
 <span>{f.q}</span>
 <ChevronRight size={18} className="text-nanda-muted group-open:rotate-90 transition-transform flex-shrink-0" />
 </summary>
 <p className="mt-4 text-nanda-inkSoft leading-relaxed">{f.a}</p>
 </details>
 ))}
 </div>
 </div>
 )}
 </article>
 </div>
 </section>
 ) : (
 /* Legacy: simple body[] paragraphs */
 <section>
 <div className="mx-auto max-w-3xl px-6 sm:px-12 pb-24 md:pb-32 space-y-6 text-nanda-inkSoft text-lg md:text-xl font-light leading-[1.75]">
 {(a.body || []).map((p, i) => (
 <FadeUp key={i} delay={i * 0.05}><p>{p}</p></FadeUp>
 ))}
 </div>
 </section>
 )}

 {/* Newsletter, discrete signup banner */}
 <NewsletterBanner l={l} />

 {/* Related articles from Carnet */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28">
 <div className="label-overline text-nanda-terracotta mb-3">{l("À lire également", "Also read", "Ler também")}</div>
 <h2 className="font-sans font-light tracking-tight text-3xl md:text-4xl leading-[1.1] text-nanda-ink mb-10">
 {l("Dans le Carnet", "In the Journal", "No Caderno")}
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
 {ARTICLES.filter((x) => x.slug !== a.slug).slice(0, 3).map((r) => (
 <Link key={r.slug} to={`/carnet/${r.slug}`} className="group block" data-testid={`related-${r.slug}`}>
 <div className="aspect-[4/3] overflow-hidden rounded-3xl bg-nanda-sand mb-4">
 <img src={r.cover} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
 </div>
 <div className="label-overline text-nanda-muted mb-2">{r.category} · {r.readTime} min</div>
 <h3 className="font-sans font-medium tracking-tight text-xl text-nanda-ink group-hover:text-nanda-terracotta transition-colors leading-snug">{r.title}</h3>
 </Link>
 ))}
 </div>
 </div>
 </section>

 {/* Next article strip */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <Link to={`/carnet/${next.slug}`} className="block hover:bg-nanda-sand transition-colors duration-500" data-testid="article-next">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28 flex flex-col md:flex-row md:items-center justify-between gap-8">
 <div>
 <div className="label-overline text-nanda-muted mb-3">{l("Article suivant", "Next article", "Próximo artigo")}</div>
 <div className="font-sans font-light tracking-tight text-3xl md:text-5xl text-nanda-ink max-w-2xl text-balance">{next.title}</div>
 </div>
 <ArrowUpRight size={36} strokeWidth={1.2} className="text-nanda-ink shrink-0" />
 </div>
 </Link>
 </section>
 </div>
 );
}

/* ------------------------------------------------------------------ */
/* NewsletterBanner, discrete inline signup at end of every article */
/* ------------------------------------------------------------------ */
function NewsletterBanner({ l }) {
 const [email, setEmail] = useState("");
 const [state, setState] = useState("idle"); // idle | submitting | done

 const handleSubmit = (e) => {
 e.preventDefault;
 if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
 setState("submitting");
 // Store locally (UI-only signup, no backend). Non-blocking.
 try {
 const key = "nanda-sa:newsletter";
 const prev = JSON.parse(localStorage.getItem(key) || "[]");
 if (!prev.includes(email)) prev.push(email);
 localStorage.setItem(key, JSON.stringify(prev));
 } catch (_) { /* no-op */ }
 setTimeout(() => setState("done"), 400);
 };

 return (
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid="article-newsletter">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 py-16 md:py-20">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
 <div className="md:col-span-5">
 <div className="label-overline text-nanda-terracotta mb-3">
 {l("Newsletter", "Newsletter", "Newsletter")}
 </div>
 <h3 className="font-sans font-light tracking-tight text-2xl md:text-3xl leading-tight text-nanda-ink">
 {l(
 "Recevoir les prochains carnets par email",
 "Receive our next Journal entries by email",
 "Receber os próximos cadernos por email"
 )}
 </h3>
 </div>
 <div className="md:col-span-7">
 <p className="text-nanda-inkSoft text-sm md:text-base leading-relaxed mb-6">
 {l(
 "Récits d'ingrédients, filières du Sénégal et de Guinée-Bissau, coulisses de la maison. Un envoi par mois, sans bruit, désabonnement en un clic.",
 "Ingredient stories, Senegal and Guinea-Bissau sectors, backstage of the house. One email a month, no noise, one-click unsubscribe.",
 "Histórias de ingredientes, fileiras do Senegal e da Guiné-Bissau, bastidores da casa. Um envio por mês, sem ruído, cancelamento com um clique."
 )}
 </p>
 {state === "done" ? (
 <div className="flex items-center gap-3 text-nanda-ink border-b border-nanda-terracotta pb-3" data-testid="newsletter-success">
 <Check size={18} strokeWidth={1.5} className="text-nanda-terracotta" />
 <span className="font-sans font-medium tracking-tight text-lg">
 {l("Merci, à très bientôt dans votre boîte de réception.", "Thank you, see you soon in your inbox.", "Obrigado, até breve na sua caixa de entrada.")}
 </span>
 </div>
 ) : (
 <form onSubmit={handleSubmit} className="flex items-center gap-4 border-b border-nanda-line focus-within:border-nanda-terracotta transition-colors pb-3" data-testid="newsletter-form">
 <input
 type="email"
 required
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 placeholder={l("votre.adresse@email.com", "your.address@email.com", "o.seu.endereco@email.com")}
 aria-label={l("Adresse email", "Email address", "Endereço email")}
 className="flex-1 bg-transparent text-nanda-ink placeholder:text-nanda-muted/70 py-2 text-base md:text-lg font-light focus:outline-none"
 data-testid="newsletter-email-input"
 />
 <button
 type="submit"
 disabled={state === "submitting"}
 className="label-overline text-nanda-ink hover:text-nanda-terracotta transition-colors flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
 data-testid="newsletter-submit"
 >
 {state === "submitting"
 ? l("Envoi…", "Sending…", "A enviar…")
 : l("S'inscrire", "Subscribe", "Subscrever")}
 <ArrowUpRight size={14} strokeWidth={1.5} />
 </button>
 </form>
 )}
 <div className="mt-3 text-xs text-nanda-muted">
 {l(
 "Nous respectons votre vie privée. Vos données ne sont ni vendues ni partagées.",
 "We respect your privacy. Your data is neither sold nor shared.",
 "Respeitamos a sua privacidade. Os seus dados não são vendidos nem partilhados."
 )}
 </div>
 </div>
 </div>
 </div>
 </section>
 );
}
